import React, {useCallback, useContext, useRef} from "react";
import {useTranslate} from "@tolgee/react";
import {LocalMapContext} from "view/context/LocalMapContext";
import {green, grey, orange} from "@material-ui/core/colors";
import {Divider, Typography} from "@material-ui/core";
import FlexBox from "view/components/Layout/FlexBox";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import CheckIcon from "view/images/CheckInCircleIcon";
import CircleIcon from "view/images/CircleIcon";
import AlternateEmailIcon from "@material-ui/icons/AlternateEmail";
import timelineRepository, {
  UnpackedAction,
} from "api/shows/stands/tasks/timeline";
import FileIcon from "view/components/FileIcon";
import {UnpackedAttachmentFile} from "ts/api/attachmentFile";
import {getReadableString} from "constraints/general";
import {
  IShow,
  ITaskTimeline,
  TaskStatus,
  TaskTimeLineType,
  Roles,
} from "@evennt-tech/api-v2-core";
import {formatISODate} from "tools/formats/formatDate";
import useDateFnsLocale from "hooks/useDateFnsLocale";
import TooltipTitle from "view/apps/Organiser/pages/Settings/EmbeddedShowList/components/ShowCard/TooltipTitle";
import Avatar from "view/components/Avatar";
import {sanitizeHtml} from "view/components/SafeHtml";

import composeActionName from "./composeActionName";
import useClasses from "./styles";
import unpackFiles from "./unpackFiles";

export default function useMapTimeline(show: IShow, standId?: string) {
  const localMap = useContext(LocalMapContext);
  const {t} = useTranslate();
  const locale = useDateFnsLocale();
  const readableString = `${getReadableString(show)}, HH:mm`;
  const actionsMap = useRef<Map<string, UnpackedAction>>(new Map());
  const classes = useClasses();

  return useCallback(
    async (action: ITaskTimeline, index: number, timeline: ITaskTimeline[]) => {
      const {type, createdAt} = action;

      const actionName = composeActionName(action, t);

      const isEmail = action.role === Roles.EMAIL;

      if (!isEmail && !actionsMap.current.get(action.id)) {
        actionsMap.current.set(
          action.id,
          // Pass showId/standId explicitly so the exhibitor-connection
          // lookup inside unpackAction doesn't fire with empty params.
          await timelineRepository.unpackAction(action, localMap, {
            showId: show.id,
            standId,
          })
        );
      }
      const unpackedAction: UnpackedAction | undefined = isEmail
        ? {
            role: action.role as Roles,
            createdAt,
            companyName: action.email as string,
            producedBy: t("common.tasks.multi_reviewer.user"),
          }
        : actionsMap.current.get(action.id);

      if (!unpackedAction) {
        return null;
      }

      let actionContent: React.ReactNode = actionName;

      const isTaskReviewType =
        type === TaskTimeLineType.APPROVE ||
        type === TaskTimeLineType.REJECT ||
        type === TaskTimeLineType.REVISE;

      if (isTaskReviewType) {
        const comment = action.comment ? (
          <Typography className={classes.mt1}>
            <div
              className={classes.message}
              dangerouslySetInnerHTML={{__html: sanitizeHtml(action.comment)}}
            />
          </Typography>
        ) : null;

        const files =
          !!action.files?.length &&
          unpackFiles(action?.files)?.map((file) => (
            <FlexBox
              key={`${file.name}-${file.link}`}
              className={`${classes.mt1} ${classes.fileBox}`}
            >
              <FlexBox
                gap={2}
                alignCenter
                justifyContent="flex-start"
                flexGrow={1}
              >
                <FileIcon
                  extension={file.type as UnpackedAttachmentFile["extension"]}
                />
                <Typography className={classes.fileTypeAndSize}>
                  {file.type}, {file.size}
                </Typography>
              </FlexBox>
              <FlexBox
                gap={2}
                alignCenter
                justifyContent="flex-end"
                flexGrow={1}
              >
                <Typography className={classes.fileName}>
                  {truncateName(file.name)}
                </Typography>
                <a
                  href={file.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.fileLink}
                >
                  <OpenInNewIcon htmlColor={grey[500]} />
                </a>
              </FlexBox>
            </FlexBox>
          ));
        actionContent = (
          <FlexBox column>
            <Typography className={classes.mt1}>{actionName}</Typography>
            {comment}
            {files}
          </FlexBox>
        );
      }

      const isNoContentAction =
        type === TaskTimeLineType.MOVE_TO_IN_REVIEW ||
        type === TaskTimeLineType.MOVE_TO_WAITING_FOR_APPROVAL ||
        type === TaskTimeLineType.UPDATE_TASK_RESPONSES ||
        type === TaskTimeLineType.MOVE_TO_TODO ||
        type === TaskTimeLineType.EDIT_TASK_DESCRIPTION;
      if (isNoContentAction) {
        actionContent = (
          <Typography
            className={`${classes.mt1} ${classes.noContentActionDescription}`}
          >
            {actionName}
          </Typography>
        );
      }

      if (type === TaskTimeLineType.UPDATE_SUB_TASK_RESPONSE) {
        actionContent = (
          <FlexBox column className={classes.mt1}>
            <Typography>{actionName}</Typography>
            <FlexBox
              alignCenter
              mt={1}
              gap={2}
              className={classes.subtaskDetailsContainer}
            >
              {action.status && taskStatusIconsMap[action.status]}
              <Typography className={classes.subtaskName}>
                {action.name}
              </Typography>
            </FlexBox>
          </FlexBox>
        );
      }

      return (
        <FlexBox column key={index}>
          <FlexBox justifyContent="space-between" alignCenter>
            <FlexBox gap={2} maxWidth="60%" alignCenter>
              {isEmail ? (
                <AlternateEmailIcon className={classes.userLogoEmail} />
              ) : (
                <Avatar
                  name={
                    !unpackedAction.logo ? unpackedAction.producedBy : undefined
                  }
                  className={classes.userLogo}
                  src={unpackedAction.logo}
                />
              )}

              <FlexBox column maxWidth="90%">
                <span style={{maxWidth: "100%"}}>
                  <TooltipTitle
                    textProps={{variant: "h3"}}
                    className={classes.producedBy}
                    name={[
                      unpackedAction?.producedBy,
                      unpackedAction?.companyName,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  />
                </span>
                <Typography className={classes.Roles}>
                  {unpackedAction.role}
                </Typography>
              </FlexBox>
            </FlexBox>
            <Typography className={classes.actionDate}>
              {formatISODate(unpackedAction.createdAt, readableString, locale)}
            </Typography>
          </FlexBox>
          {actionContent}
          {index !== timeline.length - 1 && (
            <Divider className={classes.divider} />
          )}
        </FlexBox>
      );
    },
    [classes, readableString, localMap, show.id, standId, t, locale]
  );
}

const taskStatusIconsMap: Record<TaskStatus, JSX.Element> = {
  [TaskStatus.DONE]: <CheckIcon svgProps={{fill: green[500]}} />,
  [TaskStatus.TODO]: <CircleIcon color="rgba(0,0,0,0.2)" />,
  [TaskStatus.WAITING_FOR_APPROVAL]: <CircleIcon color={orange[500]} />,
  [TaskStatus.IN_REVIEW]: <CheckIcon svgProps={{fill: orange[500]}} />,
};

function truncateName(name: string) {
  const maxCountOfSymbols = 32;
  const halfOfName = maxCountOfSymbols / 2;
  if (name.length <= maxCountOfSymbols) {
    return name;
  }
  const firstPart = name.substring(0, halfOfName);
  const secondPart = name.substring(name.length - halfOfName, name.length - 1);
  return `${firstPart}...${secondPart}`;
}

export {composeActionName};
